import { CornerDownRight, Eye, EyeOff } from "lucide-react";
import * as React from "react";

import { useReviewSheet } from "./ReviewSheetContext";

import { useTheme } from "@/components/theme-provider";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { DEFAULT_GST } from "@/constants/constants";
import { hiddenAmountSign } from "@/constants/project-register.constant";
import { IItems } from "@/interfaces/items.interface";
import { MainCost } from "@/interfaces/project-register.interface";
import { cn } from "@/lib/utils";
import { formatFinancialValue } from "@/utils/helpers";

const CustomTableHeader = ({
  isExport = false,
  isOptional = false,
}: {
  isExport: boolean;
  isOptional?: boolean;
}): JSX.Element => {
  const { theme: skin } = useTheme();
  const { setIsHideAmount, hideAmount } = useReviewSheet();

  const commonClasses = cn(
    "text-primary uppercase  ",
    skin === "dark" && "text-white",
    skin !== "dark" ? "bg-[#E4E8F2]" : "bg-primaryLight",
    "border-r border-r-white cursor-pointer",
  );

  const renderIcon = (): JSX.Element | null => {
    if (isExport) {
      return null;
    }

    return hideAmount ? (
      <EyeOff className='w-5 h-5 ml-2 cursor-pointer' />
    ) : (
      <Eye className='w-5 h-5 ml-2 cursor-pointer' />
    );
  };

  return (
    <TableHeader
      className={cn(skin !== "dark" ? "bg-[#E4E8F2]" : "bg-primaryLight")}
    >
      {isOptional && isExport && (
        <TableRow
          className={commonClasses + "text-center rounded-md !bg-transparent"}
        >
          <TableHead
            className={" border-b border-b-white  " + commonClasses}
            colSpan={6}
          >
            Additional Optional Cost Ex GST
          </TableHead>
        </TableRow>
      )}
      <TableRow>
        <TableHead className={commonClasses}>Items</TableHead>
        <TableHead className={commonClasses} style={{ width: "280px" }}>
          Demolition
        </TableHead>
        <TableHead className={commonClasses}>Unit</TableHead>
        <TableHead className={commonClasses}>Quantity</TableHead>
        <TableHead className={commonClasses}>Rate</TableHead>
        <TableHead
          className={commonClasses}
          onClick={() => !isExport && setIsHideAmount(!hideAmount)}
        >
          <div className={cn(!isExport ? "flex" : "")}>
            Total Cost ($)
            {renderIcon()}
          </div>
        </TableHead>
      </TableRow>
    </TableHeader>
  );
};

export const CustomReviewEstimateTable = ({
  data,
  itemsList,
  isOptional = false,
  isExport = false,
  className,
}: {
  data: MainCost[];
  itemsList: IItems[];
  isOptional?: boolean;
  isExport?: boolean;
  className?: string;
}): JSX.Element => {
  const commonTableRowClassName = "even:bg-transparent";

  const { setDetailSelected, hideAmount } = useReviewSheet();
  const total = data?.reduce((acc, curr) => acc + curr.total, 0);
  const gst = total * (DEFAULT_GST / 100);

  return (
    <>
      <div className={cn("rounded-md border", className)}>
        <Table>
          <CustomTableHeader isExport={isExport} isOptional={isOptional} />
          <TableBody>
            {data?.map((item, mainCostIndex) => (
              <React.Fragment key={mainCostIndex}>
                <TableRow
                  className={
                    (cn(commonTableRowClassName),
                    mainCostIndex > 0 ? "border-t" : "")
                  }
                >
                  <TableCell
                    colSpan={isExport ? 1 : 5}
                    className={cn(isExport ? "border-r" : "")}
                  >
                    {isExport ? (
                      <span className='mr-3 '>
                        {(mainCostIndex + 1).toString().padStart(2, "0")}.
                      </span>
                    ) : (
                      item?.name
                    )}
                  </TableCell>
                  {isExport && <TableCell colSpan={4}>{item?.name}</TableCell>}
                  <TableCell className='border-l pl-9'>
                    {hideAmount
                      ? hiddenAmountSign
                      : formatFinancialValue(item.total)}
                  </TableCell>
                </TableRow>
                {item?.details?.length > 0 &&
                  item?.details?.map((detail, detailIndex) => {
                    if (isExport && !detail?.selected) {
                      return null;
                    }

                    return (
                      <React.Fragment key={detailIndex}>
                        <TableRow className={cn(commonTableRowClassName)}>
                          <TableCell
                            className='m-auto border-r'
                            role='checkbox'
                          >
                            {!isExport && (
                              <Checkbox
                                checked={detail.selected || false}
                                onCheckedChange={() =>
                                  setDetailSelected(
                                    mainCostIndex,
                                    detailIndex,
                                    isOptional,
                                  )
                                }
                                className='mt-2'
                              />
                            )}
                          </TableCell>
                          <TableCell
                            className={cn(
                              "border-r ",
                              isExport ? "flex gap-2" : "",
                            )}
                          >
                            {isExport && (
                              <CornerDownRight
                                strokeWidth={3}
                                className='w-4 h-4 text-gray-500'
                              />
                            )}
                            {detail?.isCustom
                              ? detail?.item
                              : itemsList.find((i) => i.id === detail?.item)
                                  ?.title}
                          </TableCell>
                          <TableCell className='border-r'>
                            {detail.unit}
                          </TableCell>
                          <TableCell className='border-r'>
                            {detail.quantity}
                          </TableCell>
                          <TableCell className='border-r'>
                            {detail.price}
                          </TableCell>
                          <TableCell>
                            {hideAmount
                              ? hiddenAmountSign
                              : formatFinancialValue(detail.total)}
                          </TableCell>
                        </TableRow>
                        {/* {detail?.customDetails &&
                          detail?.customDetails?.length > 0 &&
                          detail?.customDetails?.map((customDetail, index) => {
                            return (
                              <TableRow key={index}>
                                <TableCell className='border-r'></TableCell>
                                <TableCell className='flex items-center gap-2 border-r'>
                                  <span>
                                    <ArrowRight className='w-5 h-5' />
                                  </span>
                                  {z
                                    .string()
                                    .uuid()
                                    .safeParse(customDetail?.item).success
                                    ? itemsList.find(
                                        (i) => i.id === customDetail?.item,
                                      )?.title
                                    : customDetail?.item}
                                </TableCell>
                                <TableCell className='border-r'>
                                  {customDetail.unit}
                                </TableCell>
                                <TableCell className='border-r'>
                                  {customDetail.quantity}
                                </TableCell>
                                <TableCell className='border-r'>
                                  {customDetail.price}
                                </TableCell>
                                <TableCell>
                                  {hideAmount && !isExport
                                    ? hiddenAmountSign
                                    : customDetail.total}
                                </TableCell>
                              </TableRow>
                            );
                          })} */}
                      </React.Fragment>
                    );
                  })}
                <TableRow
                  className={cn(
                    commonTableRowClassName,
                    "hover:bg-transparent",
                  )}
                >
                  <TableCell colSpan={6} className='w-full text-ellipsis '>
                    <p className='flex gap-2  max-w-[800px]'>
                      Note:
                      <span className='text-gray-400 break-words word-break line-clamp-3'>
                        {item?.note}
                      </span>
                    </p>
                  </TableCell>
                </TableRow>
              </React.Fragment>
            ))}
            {!isOptional && (
              <>
                <TableRow className={cn(commonTableRowClassName, "border-y")}>
                  <TableCell colSpan={5} className='text-right border-r'>
                    Total ($)
                  </TableCell>
                  <TableCell>
                    {hideAmount
                      ? hiddenAmountSign
                      : formatFinancialValue(total + gst)}
                  </TableCell>
                </TableRow>
                <TableRow className={cn(commonTableRowClassName, "border-y")}>
                  <TableCell colSpan={5} className='text-right border-r'>
                    GST (%)
                  </TableCell>
                  <TableCell>
                    {" "}
                    {hideAmount ? hiddenAmountSign : DEFAULT_GST}
                  </TableCell>
                </TableRow>
                <TableRow className={cn(commonTableRowClassName)}>
                  <TableCell colSpan={5} className='text-right border-r'>
                    Total Amount ($) (Exclusive GST)
                  </TableCell>
                  <TableCell>
                    {hideAmount
                      ? hiddenAmountSign
                      : formatFinancialValue(
                          data?.reduce((acc, curr) => acc + curr.total, 0),
                        )}
                  </TableCell>
                </TableRow>
              </>
            )}
          </TableBody>
        </Table>
      </div>
    </>
  );
};

const ReviewEstimateSheetTable = (): JSX.Element => {
  const { selectedData, itemsList } = useReviewSheet();

  // if (!selectedData) {
  //   return (
  //     <div className='min-h-screen '>
  //       <Loader2 className='w-5 h-5 animate-spin' />
  //     </div>
  //   );
  // }

  return (
    <div className='space-y-4 '>
      <CustomReviewEstimateTable
        data={selectedData?.mainCost || []}
        itemsList={itemsList}
      />
      {selectedData?.additionalCost?.length !== 0 && (
        <>
          <h1 className='text-lg'>Additional Optional Cost Ex GST</h1>
          <CustomReviewEstimateTable
            data={selectedData?.additionalCost ?? []}
            itemsList={itemsList}
            isOptional
          />
        </>
      )}
    </div>
  );
};

export default ReviewEstimateSheetTable;
